import { useCallback, useEffect, useState } from "react";
import { FileDown, Lock, Plus, Printer, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/fsl/Primitives";
import { EmptyState, LoadingState } from "@/components/fsl/States";
import { api, apiError } from "@/lib/api";
import { FinanceDashboard } from "@/components/fsl/finance/FinanceDashboard";
import { EntryDialog } from "@/components/fsl/finance/EntryDialog";
import { AiComposer } from "@/components/fsl/finance/AiComposer";
import { EntriesTable } from "@/components/fsl/finance/EntriesTable";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export default function Finance() {
  const [access, setAccess] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(0);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => { api.get("/finance/access").then((r) => setAccess(r.data)).catch(() => setAccess({ allowed: false })); }, []);
  const load = useCallback(async () => {
    try {
      const [d, e] = await Promise.all([api.get("/finance/dashboard", { params: { year } }), api.get("/finance/entries", { params: { year, month: month || undefined, kind: kind || undefined, q: q || undefined } })]);
      setDash(d.data); setRows(e.data);
    } catch (err) { toast.error(apiError(err)); }
  }, [year, month, kind, q]);
  useEffect(() => { if (access?.allowed) load(); }, [access, load]);
  const download = async (ext) => {
    setPdfBusy(true);
    try { const r = await api.get(`/finance/export.${ext}`, { params: { year }, responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `FSL_Contabilita_${year}.${ext}`; a.click(); URL.revokeObjectURL(a.href); } catch (e) { toast.error(apiError(e)); } finally { setPdfBusy(false); }
  };
  if (!access) return <LoadingState />;
  if (!access.allowed) return <EmptyState icon={Lock} title="Area riservata" description="La contabilità interna FSL è visibile solo agli amministratori autorizzati." />;
  return (
    <div data-testid="finance-page">
      <PageHeader kicker="Future Stars League · riservato" title="Contabilità interna" subtitle="Costi, entrate e ricorrenze della lega. Nulla di questa sezione è visibile fuori dagli account autorizzati." actions={<>
        <button type="button" className="btn-ghost h-10" disabled={pdfBusy} onClick={() => download("xlsx")} data-testid="finance-export-xlsx"><FileDown className="h-4 w-4" /> Excel</button>
        <button type="button" className="btn-ghost h-10" disabled={pdfBusy} onClick={() => download("pdf")} data-testid="finance-export-pdf"><Printer className="h-4 w-4" /> PDF</button>
        <button type="button" className="btn-ghost h-10 border-fsl-danger/50 text-fsl-danger" onClick={() => setDialog({ kind: "cost" })} data-testid="finance-new-cost"><Plus className="h-4 w-4" /> Nuovo costo</button>
        <button type="button" className="btn-gold h-10" onClick={() => setDialog({ kind: "income" })} data-testid="finance-new-income"><Plus className="h-4 w-4" /> Nuova entrata</button>
      </>} />
      {!dash ? <LoadingState /> : (
        <div className="space-y-6">
          <FinanceDashboard dash={dash} />
          <AiComposer cats={access} onSaved={load} />
          <section className="space-y-3" data-testid="finance-register">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="fsl-section-title !text-lg mr-2">Registro</h3>
              <select className="fsl-input h-9 w-24" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="finance-year">{dash.years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
              <select className="fsl-input h-9 w-32" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="finance-month"><option value={0}>Tutto l'anno</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
              <div className="inline-flex rounded-full border border-white/15 p-0.5">{[["", "Tutti"], ["income", "Entrate"], ["cost", "Uscite"]].map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} className={`h-8 px-3 rounded-full text-xs font-semibold ${kind === k ? "bg-fsl-gold text-ink-950" : "text-fsl-slate hover:text-fsl-white"}`} data-testid={`finance-filter-${k || "all"}`}>{l}</button>)}</div>
              <label className="relative ml-auto"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-fsl-slate" /><input className="fsl-input h-9 pl-8 w-56" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="finance-search" /></label>
            </div>
            {!rows ? <LoadingState /> : <EntriesTable rows={rows} onEdit={(e) => setDialog(e)} onDeleted={load} onChanged={load} />}
          </section>
        </div>
      )}
      <EntryDialog open={!!dialog} onClose={() => setDialog(null)} entry={dialog} cats={access} onSaved={load} />
    </div>
  );
}
